import {Box, type BoxProps, Typography} from '@mui/material';
import React from 'react';

export type SectionProps = Omit<BoxProps, 'title'> & {
  /** The h2 that names the region. */
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  /** The h2's id, for a control inside that the title names. */
  titleId?: string;
  /**
   * The h2, for a screen that moves focus to it (what replaced a list):
   * it is then focusable by script only (tabIndex -1, no focus ring).
   */
  titleRef?: React.Ref<HTMLHeadingElement>;
};

/** A titled block of a page: <section> named by its h2. */
export const Section = ({
  title,
  actions,
  children,
  titleId,
  titleRef,
  ...rest
}: SectionProps) => {
  const generatedId = React.useId();
  const id = titleId ?? generatedId;
  return (
    <Box component="section" aria-labelledby={id} {...rest}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
        }}>
        <Typography
          id={id}
          ref={titleRef}
          tabIndex={titleRef ? -1 : undefined}
          variant="title"
          component="h2"
          sx={{color: 'text.primary'}}>
          {title}
        </Typography>
        {actions && <Box sx={{display: 'flex', gap: 1}}>{actions}</Box>}
      </Box>
      {children}
    </Box>
  );
};
